import type { SupabaseClient } from "@supabase/supabase-js";

export const PRODUCT_FEEDBACK_SCREENSHOT_BUCKET = "product-feedback-screenshots";
export const PRODUCT_FEEDBACK_SCREENSHOT_MAX_BYTES = 3 * 1024 * 1024;
export const PRODUCT_FEEDBACK_SCREENSHOT_TYPES = ["image/png", "image/jpeg"] as const;

/** The server owns the orphan budget and issues the one path Storage will accept. */
export const RESERVE_PRODUCT_FEEDBACK_SCREENSHOT_RPC = "reserve_product_feedback_screenshot";
export const RELEASE_PRODUCT_FEEDBACK_SCREENSHOT_RPC = "release_product_feedback_screenshot";

export type ProductFeedbackScreenshotMimeType =
  (typeof PRODUCT_FEEDBACK_SCREENSHOT_TYPES)[number];

export interface ProductFeedbackScreenshotUpload {
  readonly path: string;
  readonly mimeType: ProductFeedbackScreenshotMimeType;
  readonly sizeBytes: number;
}

export type ProductFeedbackScreenshotValidation =
  | { readonly ok: true; readonly mimeType: ProductFeedbackScreenshotMimeType }
  | { readonly ok: false; readonly reason: string };

export const INVALID_PRODUCT_FEEDBACK_SCREENSHOT =
  "Choose one PNG or JPEG screenshot no larger than 3 MB.";
export const SAFE_PRODUCT_FEEDBACK_SCREENSHOT_FAILURE =
  "We couldn't attach this screenshot. Remove it or try another image.";
/** Server copy for a caller already holding all three outstanding orphan slots. */
export const PRODUCT_FEEDBACK_SCREENSHOT_QUOTA_REACHED =
  "Too many screenshots are waiting to send. Finish or cancel another report, then try again.";

const KNOWN_PRODUCT_FEEDBACK_SCREENSHOT_FAILURES: ReadonlySet<string> = new Set([
  PRODUCT_FEEDBACK_SCREENSHOT_QUOTA_REACHED,
  "Authentication is required to send feedback.",
  "Feedback caller mismatch.",
  "Screenshot information is not valid.",
]);

/**
 * The reservation path is server-issued and opaque: an owner UUID segment and a per-upload UUID,
 * never the operator's filename. The client re-checks the shape so a mis-wired response can never
 * send bytes to a path this Console did not ask for.
 */
const RESERVED_SCREENSHOT_PATH =
  /^users\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(?:png|jpg)$/;

export function validateProductFeedbackScreenshot(
  file: File,
): ProductFeedbackScreenshotValidation {
  if (
    !PRODUCT_FEEDBACK_SCREENSHOT_TYPES.includes(
      file.type as ProductFeedbackScreenshotMimeType,
    ) ||
    file.size < 1 ||
    file.size > PRODUCT_FEEDBACK_SCREENSHOT_MAX_BYTES
  ) {
    return { ok: false, reason: INVALID_PRODUCT_FEEDBACK_SCREENSHOT };
  }
  return { ok: true, mimeType: file.type as ProductFeedbackScreenshotMimeType };
}

function reservedScreenshotPath(
  data: unknown,
  mimeType: ProductFeedbackScreenshotMimeType,
): string | null {
  const row = Array.isArray(data) ? data[0] : data;
  const path =
    row !== null &&
    typeof row === "object" &&
    typeof (row as { object_path?: unknown }).object_path === "string"
      ? (row as { object_path: string }).object_path
      : "";
  if (!RESERVED_SCREENSHOT_PATH.test(path)) return null;
  return path.endsWith(mimeType === "image/png" ? ".png" : ".jpg") ? path : null;
}

function screenshotFailureReason(error: unknown): string {
  const message =
    error !== null &&
    typeof error === "object" &&
    typeof (error as { message?: unknown }).message === "string"
      ? (error as { message: string }).message
      : "";
  return KNOWN_PRODUCT_FEEDBACK_SCREENSHOT_FAILURES.has(message)
    ? message
    : SAFE_PRODUCT_FEEDBACK_SCREENSHOT_FAILURE;
}

/**
 * Take one of the caller's three outstanding orphan slots. The budget, the expiry, and the path
 * are all decided server-side; this call only reports which path Storage will now accept.
 */
export async function reserveProductFeedbackScreenshot(
  client: SupabaseClient,
  mimeType: ProductFeedbackScreenshotMimeType,
): Promise<
  | { readonly ok: true; readonly path: string }
  | { readonly ok: false; readonly reason: string }
> {
  try {
    const { data, error } = await client.rpc(RESERVE_PRODUCT_FEEDBACK_SCREENSHOT_RPC, {
      p_mime_type: mimeType,
    });
    if (error !== null) {
      return { ok: false, reason: screenshotFailureReason(error) };
    }
    const path = reservedScreenshotPath(data, mimeType);
    if (path === null) {
      return { ok: false, reason: SAFE_PRODUCT_FEEDBACK_SCREENSHOT_FAILURE };
    }
    return { ok: true, path };
  } catch {
    return { ok: false, reason: SAFE_PRODUCT_FEEDBACK_SCREENSHOT_FAILURE };
  }
}

/**
 * Hand a slot back after the browser removed its own unsubmitted object. The server re-checks
 * that the object is really gone, so this is a fast path and never the lifecycle guarantee.
 */
async function releaseProductFeedbackScreenshotSlot(
  client: SupabaseClient,
  path: string,
): Promise<void> {
  try {
    await client.rpc(RELEASE_PRODUCT_FEEDBACK_SCREENSHOT_RPC, { p_object_path: path });
  } catch {
    // The slot still frees on its own: an unused reservation expires, and an object that outlives
    // the browser is deleted by the 24-hour server cleanup.
  }
}

export async function uploadProductFeedbackScreenshot(
  client: SupabaseClient,
  file: File,
): Promise<
  | { readonly ok: true; readonly upload: ProductFeedbackScreenshotUpload }
  | { readonly ok: false; readonly reason: string }
> {
  const validation = validateProductFeedbackScreenshot(file);
  if (!validation.ok) return validation;

  const reservation = await reserveProductFeedbackScreenshot(client, validation.mimeType);
  if (!reservation.ok) return reservation;

  try {
    const { error } = await client.storage
      .from(PRODUCT_FEEDBACK_SCREENSHOT_BUCKET)
      .upload(reservation.path, file, {
        cacheControl: "3600",
        contentType: validation.mimeType,
        upsert: false,
      });
    if (error !== null) {
      await releaseProductFeedbackScreenshotSlot(client, reservation.path);
      return { ok: false, reason: SAFE_PRODUCT_FEEDBACK_SCREENSHOT_FAILURE };
    }
    return {
      ok: true,
      upload: {
        path: reservation.path,
        mimeType: validation.mimeType,
        sizeBytes: file.size,
      },
    };
  } catch {
    await releaseProductFeedbackScreenshotSlot(client, reservation.path);
    return { ok: false, reason: SAFE_PRODUCT_FEEDBACK_SCREENSHOT_FAILURE };
  }
}

/**
 * Best-effort rollback for the narrow upload-before-row window, in the strict sense: Storage reads
 * in this bucket are Platform-staff only, and Postgres filters a DELETE that carries a WHERE
 * clause through the SELECT policies, so the reporter's own delete currently matches nothing. The
 * attempt is kept because it costs nothing and starts working the moment that read posture
 * changes; the 24-hour server cleanup is the deletion that actually runs. `release` re-checks that
 * the object is gone, so a refused delete leaves the slot held rather than freeing it falsely.
 */
export async function removeOrphanProductFeedbackScreenshot(
  client: SupabaseClient,
  path: string,
): Promise<void> {
  try {
    await client.storage.from(PRODUCT_FEEDBACK_SCREENSHOT_BUCKET).remove([path]);
  } catch {
    // The report failure remains the user-visible outcome. The private orphan cannot be read by
    // the submitter or another tenant, and the 24-hour server cleanup removes it.
  }
  await releaseProductFeedbackScreenshotSlot(client, path);
}
